import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../config/api';
import { FaSearch, FaUser, FaPhone, FaCalendarAlt, FaTimes } from 'react-icons/fa';

interface PatientResult {
  id: string;
  patient_id: number;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  mrn?: string;
  contact_phone: string;
  contact_email: string;
}

const formatDOB = (dob: string) => {
  if (!dob) return '—';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return dob;
  const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  return `${d.toLocaleDateString('en-AU')} (${age} yrs)`;
};

const PatientSidebarPanel: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PatientResult[]>([]);
  const [selected, setSelected] = useState<PatientResult | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiCall(`/patients/?search=${encodeURIComponent(query.trim())}&limit=10`, { method: 'GET' }, token || undefined);
        if (res.ok) {
          const data = await res.json();
          setResults(data.results || data);
        }
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [query, token]);

  const selectPatient = (p: PatientResult) => {
    setSelected(p);
    setResults([]);
    setQuery('');
  };

  const clearSelected = () => setSelected(null);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-base-300 flex-shrink-0">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-sm" />
          <input
            type="text"
            className="input input-sm input-bordered w-full pl-8 pr-3"
            placeholder="Search patient name or MRN…"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(null); }}
          />
          {query && (
            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content" onClick={() => setQuery('')}>
              <FaTimes className="text-xs" />
            </button>
          )}
        </div>

        {/* Search results dropdown */}
        {results.length > 0 && (
          <div className="mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
            {results.map(p => (
              <button
                key={p.id}
                className="w-full text-left px-3 py-2 hover:bg-base-200 transition-colors border-b border-base-300 last:border-0"
                onClick={() => selectPatient(p)}
              >
                <div className="text-sm font-medium">{p.first_name} {p.last_name}</div>
                <div className="text-xs text-base-content/60">MRN: {p.mrn || p.patient_id} · DOB: {p.date_of_birth ? new Date(p.date_of_birth).toLocaleDateString('en-AU') : '—'}</div>
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="mt-2 flex justify-center">
            <span className="loading loading-spinner loading-xs" />
          </div>
        )}
      </div>

      {/* Patient detail */}
      <div className="flex-1 overflow-y-auto p-3">
        {selected ? (
          <div className="space-y-3">
            {/* Header card */}
            <div className="card bg-primary text-primary-content">
              <div className="card-body p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-primary-content/20 flex items-center justify-center flex-shrink-0">
                      <FaUser className="text-primary-content/80" />
                    </div>
                    <div>
                      <h3 className="font-bold text-base leading-tight">{selected.first_name} {selected.last_name}</h3>
                      <p className="text-xs opacity-80">MRN: {selected.mrn || selected.patient_id}</p>
                    </div>
                  </div>
                  <button onClick={clearSelected} className="btn btn-ghost btn-xs text-primary-content/60">
                    <FaTimes />
                  </button>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="card bg-base-200">
              <div className="card-body p-3 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="font-semibold opacity-70">DOB</span>
                  <span>{formatDOB(selected.date_of_birth)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold opacity-70">Gender</span>
                  <span>{selected.gender || '—'}</span>
                </div>
                <div className="divider my-1" />
                <div className="flex items-center gap-2">
                  <FaPhone className="opacity-50 text-xs flex-shrink-0" />
                  <span>{selected.contact_phone || 'No phone'}</span>
                </div>
                {selected.contact_email && (
                  <div className="text-xs text-base-content/60 truncate">{selected.contact_email}</div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                className="btn btn-primary btn-sm flex-1 text-xs min-h-0 h-8"
                onClick={() => navigate(`/patients/${selected.id}`)}
              >
                <FaUser className="flex-shrink-0" />
                View Patient
              </button>
              <button
                className="btn btn-outline btn-sm flex-1 text-xs min-h-0 h-8"
                onClick={() => navigate(`/patients/${selected.id}#appointments`)}
              >
                <FaCalendarAlt className="flex-shrink-0" />
                Appts
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-base-content/40 text-center px-4">
            <FaUser className="text-4xl mb-3 opacity-30" />
            <p className="text-sm">Search for a patient above to view their details here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientSidebarPanel;
